import React, { useRef, useEffect, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getActiveTab, getEnvironment, hasCredentials, getLoading } from '../../store/ClientCredentials/selectors';
import { selectUser } from '../../store/auth/selectors';
import { setActiveTab, setEnvironment } from '../../store/ClientCredentials/reducer';
import { MerchantManager } from '../../components/Merchants';
import ApplicationManager from '../../components/Applications/ApplicationManager';
import { SubscriptionManager } from '../../components/Subscriptions';
import './ClientCredentials.css';

const ClientCredentials: React.FC = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const activeTab = useSelector(getActiveTab);
    const environment = useSelector(getEnvironment);
    const user = useSelector(selectUser);
    const hasMerchants = useSelector(hasCredentials);
    const merchantsLoading = useSelector(getLoading);

    const userId = user?.id || null;
    const [sectionsInitialized, setSectionsInitialized] = useState(false);
    const [prevEnvironment, setPrevEnvironment] = useState(environment);

    // Reset synchronously during render when environment changes.
    // preventing duplicate API calls (children effects fire before parent effects).
    if (prevEnvironment !== environment) {
        setPrevEnvironment(environment);
        setSectionsInitialized(false);
    }

    const showDependentSections = Boolean(userId) && (hasMerchants || sectionsInitialized);

    useEffect(() => {
        if (Boolean(userId) && hasMerchants && !sectionsInitialized) {
            setSectionsInitialized(true);
        }
    }, [userId, hasMerchants, sectionsInitialized]);

    // Reset when user logs out
    useEffect(() => {
        if (!userId) {
            setSectionsInitialized(false);
        }
    }, [userId]);

    const [activeNavItem, setActiveNavItem] = useState<string>('merchant-heading');

    const merchantSectionLabel = environment === 'production'
        ? t('clientCredentials.storeMerchantId')
        : t('clientCredentials.testStoreMerchantId');

    const sectionNames = [
        merchantSectionLabel,
        t('clientCredentials.clientCredentialsOAuth'),
        t('clientCredentials.subscriptionsApiKey'),
    ];

    const handleEnvironmentToggle = (env: 'sandbox' | 'production') => {
        dispatch(setEnvironment(env));
    };

    // Track which section is visible on scroll and update activeTab
    const isScrollingToSection = useRef(false);

    const handleScroll = useCallback(() => {
        if (isScrollingToSection.current) return;

        const sectionIds = ['merchant-section', 'application-section', 'subscription-section'];

        // If scrolled to the bottom of the page, activate the last section
        const isAtBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 50;
        if (isAtBottom) {
            if (activeTab !== sectionIds.length - 1) {
                dispatch(setActiveTab(sectionIds.length - 1));
                setActiveNavItem('subscription-heading');
            }
            return;
        }

        let visibleSection = 0;
        for (let i = sectionIds.length - 1; i >= 0; i--) {
            const el = document.getElementById(sectionIds[i]);
            if (el) {
                const rect = el.getBoundingClientRect();
                if (rect.top <= 200) {
                    visibleSection = i;
                    break;
                }
            }
        }

        if (visibleSection !== activeTab) {
            dispatch(setActiveTab(visibleSection));
            const headingMap = ['merchant-heading', 'app-heading', 'subscription-heading'];
            setActiveNavItem(headingMap[visibleSection]);
        }
    }, [activeTab, dispatch]);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    const handleTabChange = (tab: number, navItem?: string) => {
        isScrollingToSection.current = true;
        dispatch(setActiveTab(tab));
        if (navItem) setActiveNavItem(navItem);
        const sectionIds = ['merchant-section', 'application-section', 'subscription-section'];
        const element = document.getElementById(sectionIds[tab]);

        if (element) {
            const yOffset = -130;
            const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
            window.scrollTo({ top: y, behavior: 'smooth' });
        }

        // Re-enable scroll detection after animation completes
        setTimeout(() => {
            isScrollingToSection.current = false;
        }, 800);
    };

    return (
        <div className="client-credentials-container">
            {/* 2-Column Layout: Left Nav + Right Content */}
            <div className="credentials-layout">
                {/* Left: Vertical Navigation */}
                <aside className="credentials-left-nav">
                    <nav className="credentials-nav-tabs">
                        <button
                            onClick={() => handleTabChange(0, 'merchant-heading')}
                            className={activeTab === 0 ? 'credentials-nav-item active' : 'credentials-nav-item'}
                        >
                            {merchantSectionLabel}
                        </button>
                        <button
                            onClick={() => handleTabChange(1, 'app-heading')}
                            className={activeTab === 1 ? 'credentials-nav-item active' : 'credentials-nav-item'}
                        >
                            {t('clientCredentials.clientCredentialsOAuth')}
                        </button>
                        <button
                            onClick={() => handleTabChange(2, 'subscription-heading')}
                            className={activeTab === 2 ? 'credentials-nav-item active' : 'credentials-nav-item'}
                        >
                            {t('clientCredentials.subscriptionsApiKey')}
                        </button>
                    </nav>
                </aside>

                {/* Right: Content Area */}
                <main className="credentials-content-area">
                    {/* Mobile-only Sandbox/Production toggle */}
                    <div className="credentials-env-toggle-mobile">
                        <span className={environment === 'sandbox' ? 'toggle-label active sandbox-active' : 'toggle-label'}>
                            {t('merchants.sandbox', { defaultValue: 'Sandbox' })}
                        </span>
                        <label className="toggle-switch">
                            <input
                                type="checkbox"
                                checked={environment === 'production'}
                                onChange={(e) => handleEnvironmentToggle(e.target.checked ? 'production' : 'sandbox')}
                            />
                            <span className={environment === 'production' ? 'toggle-slider active production' : 'toggle-slider sandbox'}></span>
                        </label>
                        <span className={environment === 'production' ? 'toggle-label active production-active' : 'toggle-label'}>
                            {t('merchants.production', { defaultValue: 'Production' })}
                        </span>
                    </div>

                    {/* Page Heading inside content */}
                    <div className="credentials-page-header">
                        <div className="credentials-header-row">
                            <div className="credentials-breadcrumb">
                                <span className="breadcrumb-item">{t('clientCredentials.accessAndCredentials')}</span>
                                <span className="breadcrumb-separator">&gt;</span>
                                <span className="breadcrumb-item">{sectionNames[activeTab]}</span>
                            </div>
                            {/* Sandbox/Production toggle */}
                            <div className="credentials-env-toggle">
                                <span className={environment === 'sandbox' ? 'toggle-label active sandbox-active' : 'toggle-label'}>
                                    {t('merchants.sandbox', { defaultValue: 'Sandbox' })}
                                </span>
                                <label className="toggle-switch">
                                    <input
                                        type="checkbox"
                                        checked={environment === 'production'}
                                        onChange={(e) => handleEnvironmentToggle(e.target.checked ? 'production' : 'sandbox')}
                                    />
                                    <span className={environment === 'production' ? 'toggle-slider active production' : 'toggle-slider sandbox'}></span>
                                </label>
                                <span className={environment === 'production' ? 'toggle-label active production-active' : 'toggle-label'}>
                                    {t('merchants.production', { defaultValue: 'Production' })}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Section 1: Test Store/Merchant ID */}
                    <div id="merchant-section" className="credentials-section">
                        <MerchantManager />
                    </div>

                    {/* Section 2: Client Credentials (OAuth2) */}
                    <div id="application-section" className="credentials-section">
                        {showDependentSections ? (
                            <ApplicationManager
                                environment={environment}
                                userId={userId}
                            />
                        ) : (
                            <div className="application-manager">
                                <div className="application-manager-header">
                                    <h3 className="application-manager-title">
                                        {t('clientCredentials.clientCredentialsOAuth')}
                                    </h3>
                                    <hr className="section-title-divider" />
                                    <h2 className="application-title">{t('applications.createNew', { defaultValue: 'Create New' })}</h2>
                                </div>
                                {userId && !merchantsLoading && !hasMerchants ? (
                                    <p className="no-access-message">{t('clientCredentials.noMerchantsNoAccess')}</p>
                                ) : !userId && !hasMerchants ? (
                                    <p className="auth-required-text">{t('clientCredentials.signInToManageApplications')}</p>
                                ) : null}
                                <div className="application-list-section">
                                    <h2 className="application-list-title">{t('applications.manageClients', { defaultValue: 'Manage Clients' })}</h2>
                                    {!userId && !hasMerchants && (
                                        <p className="auth-required-text">{t('clientCredentials.signInToManageApplications')}</p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Section 3: Subscriptions (API key) */}
                    <div id="subscription-section" className="credentials-section">
                        {showDependentSections ? (
                            <SubscriptionManager userId={userId} environment={environment} />
                        ) : (
                            <div className="application-manager">
                                <div className="application-manager-header">
                                    <h3 className="application-manager-title">
                                        {t('clientCredentials.subscriptionsApiKey')}
                                    </h3>
                                    <hr className="section-title-divider" />
                                    <h2 className="subscription-title">{t('subscriptions.newSubscription', { defaultValue: 'New Subscription' })}</h2>
                                </div>
                                {userId && !merchantsLoading && !hasMerchants ? (
                                    <p className="no-access-message">{t('clientCredentials.noMerchantsNoAccess')}</p>
                                ) : !userId && !hasMerchants ? (
                                    <p className="auth-required-text">{t('clientCredentials.signInToManageSubscriptions')}</p>
                                ) : null}
                                <div className="subscription-list-section">
                                    <p className="subscription-list-title">{t('subscriptions.manageSubscriptionsTitle', { defaultValue: 'Manage Subscriptions' })}</p>
                                    {!userId && !hasMerchants && (
                                        <p className="auth-required-text">{t('clientCredentials.signInToManageSubscriptions')}</p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </main>

                {/* Right: Section Navigation (sticky) — always rendered to preserve layout */}
                <aside className="credentials-right-nav" style={{ visibility: showDependentSections ? 'visible' : 'hidden' }}>
                    <nav className="credentials-right-nav-list">
                        <div className={activeTab === 0 ? 'right-nav-section active' : 'right-nav-section'}>
                            <button
                                onClick={() => handleTabChange(0, 'merchant-heading')}
                                className={`right-nav-heading${activeNavItem === 'merchant-heading' ? ' active-item' : ''}`}
                            >
                                {merchantSectionLabel}
                            </button>
                        </div>

                        <div className={activeTab === 1 ? 'right-nav-section active' : 'right-nav-section'}>
                            <button
                                onClick={() => handleTabChange(1, 'app-heading')}
                                className={`right-nav-heading${activeNavItem === 'app-heading' ? ' active-item' : ''}`}
                            >
                                {t('clientCredentials.clientCredentialsOAuth')}
                            </button>
                            <ol className="right-nav-sub-list">
                                <li>
                                    <button onClick={() => handleTabChange(1, 'app-create')} className={`right-nav-sub-item${activeNavItem === 'app-create' ? ' active-item' : ''}`}>
                                        {t('applications.createOrManageApplications', { defaultValue: 'Create or Manage Applications' })}
                                    </button>
                                </li>
                            </ol>
                        </div>

                        <div className={activeTab === 2 ? 'right-nav-section active' : 'right-nav-section'}>
                            <button
                                onClick={() => handleTabChange(2, 'subscription-heading')}
                                className={`right-nav-heading${activeNavItem === 'subscription-heading' ? ' active-item' : ''}`}
                            >
                                {t('clientCredentials.subscriptionsApiKey')}
                            </button>
                            <ol className="right-nav-sub-list">
                                <li>
                                    <button onClick={() => handleTabChange(2, 'sub-new')} className={`right-nav-sub-item${activeNavItem === 'sub-new' ? ' active-item' : ''}`}>
                                        {t('subscriptions.createOrManageSubscriptions', { defaultValue: 'Create or Manage Subscriptions' })}
                                    </button>
                                </li>
                            </ol>
                        </div>
                    </nav>
                </aside>
            </div>
        </div>
    );
};

export default ClientCredentials;

